/**
 * GENERAL Vuex Module
 *
 * Manages application-wide UI state including:
 * - Expandable rail navigation (50px ↔ 250px)
 * - Multi-panel system (scalable 1-5+ panels)
 * - Panel resizing with persistence
 * - Collapse/expand behavior
 * - Pin state for preventing auto-close
 *
 * @module GENERAL
 */

import { PANEL_IDS, isTagTypePanel } from '@/constants/panelIds';

// Default panel width constants
const PANEL_WIDTH_DEFAULT = 300;
const PANEL_WIDTH_MIN = 200;
const PANEL_WIDTH_MAX = 500;
const RAIL_WIDTH_COLLAPSED = 60;
const RAIL_WIDTH_EXPANDED = 250;

// === INSPECTION FILTER DOCK (Phase 3) ===
const DOCK_WIDTH = 460; // matches slide-out :size="460" (InspectionFiltersDrawer.vue:7)
const DOCK_MODE_STORAGE_KEY = 'inspection-filters-dock-mode';
const DOCK_MODES = Object.freeze({ FLOAT: 'float', DOCKED: 'docked' });

function readDockModeFromStorage() {
  // try/catch — Safari private mode throws on localStorage access. Enum-validate; default float.
  try {
    return localStorage.getItem(DOCK_MODE_STORAGE_KEY) === DOCK_MODES.DOCKED
      ? DOCK_MODES.DOCKED
      : DOCK_MODES.FLOAT;
  } catch (err) {
    return DOCK_MODES.FLOAT;
  }
}

// === QUICK SETTINGS (Persistence) ===
const QS_VISIBLE_STORAGE_KEY = 'quick-settings-visible';
const QS_PINNED_STORAGE_KEY = 'quick-settings-pinned';

function readQuickSettingsVisibleFromStorage() {
  try {
    const item = localStorage.getItem(QS_VISIBLE_STORAGE_KEY);
    return item !== null ? item === 'true' : true; // Default to true (visible)
  } catch (err) {
    return true;
  }
}

function readQuickSettingsPinnedFromStorage() {
  try {
    const item = localStorage.getItem(QS_PINNED_STORAGE_KEY);
    return item === 'true'; // Default to false (floating)
  } catch (err) {
    return false;
  }
}

export const GENERAL = {
  namespaced: true,

  state: {
    // === EXISTING STATE (preserved for backward compatibility) ===
    drawer: true,
    openList: { Viewer: true },
    isDashboardOpened: true,
    viewerSideMenuMinimized: false,

    // === RAIL STATE ===
    isRailExpanded: false, // Rail width: 50px (false) or 250px (true)
    activeGroup: null, // 'search' | 'view' | 'data' | 'management' | 'integrity' | 'settings' | null

    // === PANEL SYSTEM (Scalable) ===
    // Array of panel objects: { id, width, order }
    panels: [],
    maxPanels: 3, // Configurable: 1, 2, 3, 4, 5... (3 allows comfortable tag type comparison)
    arePanelsCollapsed: false, // Panels hidden but state preserved

    // Persisted panel widths (survives panel close/reopen)
    savedPanelWidths: {}, // { 'tags': 350, 'documents': 280 }

    // === PIN STATE ===
    isPinned: true, // Panels are always pinned (no auto-collapse on outside click)
    pinnedPanelIds: [], // Array of individually pinned panel IDs

    // === QUICK SETTINGS FLOATING UI ===
    isQuickSettingsVisible: readQuickSettingsVisibleFromStorage(), // Floating quick settings panel visibility
    isQuickSettingsPinned: readQuickSettingsPinnedFromStorage(), // Pinned to top navigation bar
    canPinQuickSettingsToTopBar: false, // Published after measuring the navigation controls
    // One-shot request to auto-open the ghost-style overlay when the Quick Settings bar
    // (re)mounts. Set by the layout's "auto-show on ghosting" watcher; consumed + cleared
    // by QuickSettingsFloating. Lives here (not in that component) because the bar is
    // v-if'd out while hidden, so it cannot react to ghosting turning on by itself.
    ghostOverlayOpenRequest: false,

    // === INSPECTION FILTER DOCK (Phase 3) ===
    // Dock preference is GLOBAL across both hosts (one physical surface). Persisted.
    inspectionFilterDockMode: readDockModeFromStorage(),
    // Which host currently reserves the gutter. null = nobody. Claimed on open/expand while
    // docked; released only-if-owner on close/collapse/destroy (both hosts stay mounted).
    // SAFETY INVARIANT: this single owner slot is correct ONLY because at most one host
    // drawer is open at a time — enforced by the host tab watchers (InspectionTagPanel.vue /
    // AvaInspectionPanel.vue `activeTab(...)` force-close their own drawerFilterOpen when
    // their tab goes inactive). If a future layout ever opens BOTH drawers simultaneously,
    // the only-if-owner RELEASE below can clobber the live owner — switch to a per-owner Set.
    inspectionFilterGutterOwner: null, // null | 'inspections' | 'ava'
    // Bumped when CmlAddEditPanel opens — the drawer watches it and minimizes (Phase 3 T3.5).
    inspectionFilterMinimizeRequestId: 0,
  },

  getters: {
    // === EXISTING GETTERS (preserved) ===
    GET_DRAWER(state) {
      return state.drawer;
    },
    GET_OPEN_LIST(state) {
      return state.openList;
    },
    GET_IS_DASHBOARD_OPENED(state) {
      return state.isDashboardOpened;
    },
    GET_VIEWER_SIDE_MENU_MINIMIZED(state) {
      return state.viewerSideMenuMinimized;
    },

    // === RAIL GETTERS ===
    GET_IS_RAIL_EXPANDED(state) {
      return state.isRailExpanded;
    },
    GET_ACTIVE_GROUP(state) {
      return state.activeGroup;
    },
    GET_RAIL_WIDTH(state) {
      return state.isRailExpanded ? RAIL_WIDTH_EXPANDED : RAIL_WIDTH_COLLAPSED;
    },

    // === PANEL GETTERS ===
    GET_PANELS(state) {
      return state.panels;
    },
    GET_MAX_PANELS(state) {
      return state.maxPanels;
    },
    GET_ARE_PANELS_COLLAPSED(state) {
      return state.arePanelsCollapsed;
    },
    GET_HAS_PANELS(state) {
      return state.panels.length > 0;
    },
    GET_CAN_OPEN_MORE_PANELS(state) {
      return state.panels.length < state.maxPanels;
    },
    GET_PANEL_BY_ID: (state) => (id) => {
      return state.panels.find((p) => p.id === id);
    },
    GET_IS_PANEL_OPEN: (state) => (id) => {
      return state.panels.some((p) => p.id === id);
    },
    GET_SAVED_PANEL_WIDTH: (state) => (id) => {
      return state.savedPanelWidths[id] || PANEL_WIDTH_DEFAULT;
    },

    // === LAYOUT CALCULATION GETTERS ===
    GET_TOTAL_PANELS_WIDTH(state) {
      // Return 0 if collapsed, otherwise sum of widths
      if (state.arePanelsCollapsed) return 0;
      return state.panels.reduce((sum, p) => sum + p.width, 0);
    },
    GET_TOTAL_NAV_WIDTH(state, getters) {
      return RAIL_WIDTH_COLLAPSED + getters.GET_TOTAL_PANELS_WIDTH;
    },

    // === PIN GETTERS ===
    GET_IS_PINNED(state) {
      return state.isPinned;
    },
    GET_IS_PANEL_PINNED: (state) => (panelId) => {
      return state.pinnedPanelIds.includes(panelId);
    },
    GET_PINNED_PANEL_IDS(state) {
      return state.pinnedPanelIds;
    },

    // === QUICK SETTINGS GETTERS ===
    GET_IS_QUICK_SETTINGS_VISIBLE(state) {
      return state.isQuickSettingsVisible;
    },
    GET_IS_QUICK_SETTINGS_PINNED(state) {
      return state.isQuickSettingsPinned;
    },
    GET_CAN_PIN_QUICK_SETTINGS_TO_TOP_BAR(state) {
      return state.canPinQuickSettingsToTopBar;
    },
    GET_GHOST_OVERLAY_OPEN_REQUEST(state) {
      return state.ghostOverlayOpenRequest;
    },

    // === INSPECTION FILTER DOCK GETTERS (Phase 3) ===
    GET_INSPECTION_FILTER_DOCK_MODE(state) {
      return state.inspectionFilterDockMode;
    },
    GET_INSPECTION_FILTER_GUTTER_OWNER(state) {
      return state.inspectionFilterGutterOwner;
    },
    GET_INSPECTION_FILTER_MINIMIZE_REQUEST_ID(state) {
      return state.inspectionFilterMinimizeRequestId;
    },
    // Store-visible portion of the right-gutter inset (px). Does NOT include the
    // route-dashboard term — a Vuex getter can't read $route, so view.vue / MainView
    // AND-in isRouteDashboardActive($route) (see @/constants/dashboards). MAINTENANCE
    // HAZARD: dockAllowed is a NEGATIVE allow-list. Any FUTURE viewport-width layout mode
    // added elsewhere will silently overflow this gutter with no compiler signal. If the
    // app ever grows one canonical "normal single-viewer layout" predicate, gate on THAT.
    GET_INSPECTION_FILTER_DOCK_INSET(state, getters, rootState, rootGetters) {
      if (state.inspectionFilterDockMode !== DOCK_MODES.DOCKED) return 0;
      if (state.inspectionFilterGutterOwner === null) return 0;
      const dockAllowed =
        !rootGetters.getShowComparasionTool && //    compare width = calc(100vw - nav)
        !rootGetters.getIsRealityDashoardShown && // reality spacer = calc(50vw - 20px)
        !rootGetters.GET_IS_DASHBOARD_FULL_SCREEN;
      return dockAllowed ? DOCK_WIDTH : 0;
    },

    // === BACKWARD COMPATIBILITY GETTERS ===
    // These maintain API compatibility with existing code
    GET_IS_PRIMARY_PALETTE_OPEN(state) {
      return state.isRailExpanded;
    },
    GET_IS_SECONDARY_PALETTE_OPEN(state) {
      return state.panels.length > 0 && !state.arePanelsCollapsed;
    },
    GET_ACTIVE_SECONDARY_PANEL(state) {
      return state.panels[0]?.id || null;
    },
    GET_IS_ANY_PALETTE_OPEN(state) {
      return state.isRailExpanded || (state.panels.length > 0 && !state.arePanelsCollapsed);
    },
  },

  mutations: {
    // === EXISTING MUTATIONS (preserved) ===
    SET_DRAWER(state, val) {
      state.drawer = val;
    },
    SET_OPEN_LIST(state, val) {
      state.openList = { ...val };
    },
    SET_IS_DASHBOARD_OPENED(state, val) {
      state.isDashboardOpened = val;
    },
    SET_VIEWER_SIDE_MENU_MINIMIZED(state, val) {
      state.viewerSideMenuMinimized = val;
    },

    // === RAIL MUTATIONS ===
    SET_RAIL_EXPANDED(state, val) {
      state.isRailExpanded = val;
    },
    SET_ACTIVE_GROUP(state, val) {
      state.activeGroup = val;
    },

    // === PANEL MUTATIONS ===
    ADD_PANEL(state, { id, width }) {
      // Use saved width if available, otherwise provided width or default
      const savedWidth = state.savedPanelWidths[id] || width || PANEL_WIDTH_DEFAULT;
      state.panels.push({
        id,
        width: savedWidth,
        order: state.panels.length,
      });
    },

    REMOVE_PANEL(state, panelId) {
      const index = state.panels.findIndex((p) => p.id === panelId);
      if (index !== -1) {
        state.panels.splice(index, 1);
        // Re-order remaining panels
        state.panels.forEach((p, i) => {
          p.order = i;
        });
      }
    },

    CLEAR_PANELS(state) {
      state.panels = [];
    },

    SET_PANEL_WIDTH(state, { panelId, width }) {
      const panel = state.panels.find((p) => p.id === panelId);
      if (panel) {
        // Clamp width to min/max
        panel.width = Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, width));
        // Persist for future opens
        state.savedPanelWidths[panelId] = panel.width;
      }
    },

    SET_MAX_PANELS(state, count) {
      state.maxPanels = Math.max(1, Math.min(5, count));
      // Trim excess panels if needed
      while (state.panels.length > state.maxPanels) {
        state.panels.pop();
      }
    },

    SET_PANELS_COLLAPSED(state, val) {
      state.arePanelsCollapsed = val;
    },

    // === PIN MUTATIONS ===
    SET_IS_PINNED(state, val) {
      state.isPinned = val;
    },
    TOGGLE_PANEL_PIN(state, panelId) {
      const index = state.pinnedPanelIds.indexOf(panelId);
      if (index === -1) {
        state.pinnedPanelIds.push(panelId);
      } else {
        state.pinnedPanelIds.splice(index, 1);
      }
    },
    UNPIN_PANEL(state, panelId) {
      const index = state.pinnedPanelIds.indexOf(panelId);
      if (index !== -1) {
        state.pinnedPanelIds.splice(index, 1);
      }
    },

    // === QUICK SETTINGS MUTATIONS ===
    SET_QUICK_SETTINGS_VISIBLE(state, val) {
      state.isQuickSettingsVisible = !!val;
      try {
        localStorage.setItem(QS_VISIBLE_STORAGE_KEY, String(!!val));
      } catch (err) {
        // Safari private mode — preference stays in-memory for this session.
      }
    },
    SET_CAN_PIN_QUICK_SETTINGS_TO_TOP_BAR(state, val) {
      state.canPinQuickSettingsToTopBar = val;
    },
    SET_QUICK_SETTINGS_PINNED(state, val) {
      state.isQuickSettingsPinned = !!val;
      try {
        localStorage.setItem(QS_PINNED_STORAGE_KEY, String(!!val));
      } catch (err) {
        // Safari private mode — preference stays in-memory for this session.
      }
    },
    SET_GHOST_OVERLAY_OPEN_REQUEST(state, val) {
      state.ghostOverlayOpenRequest = val;
    },

    // === INSPECTION FILTER DOCK MUTATIONS (Phase 3) ===
    SET_INSPECTION_FILTER_DOCK_MODE(state, mode) {
      const next = mode === DOCK_MODES.DOCKED ? DOCK_MODES.DOCKED : DOCK_MODES.FLOAT;
      state.inspectionFilterDockMode = next;
      try {
        localStorage.setItem(DOCK_MODE_STORAGE_KEY, next);
      } catch (err) {
        // Safari private mode — preference stays in-memory for this session.
      }
    },
    CLAIM_INSPECTION_FILTER_GUTTER(state, owner) {
      state.inspectionFilterGutterOwner = owner;
    },
    RELEASE_INSPECTION_FILTER_GUTTER(state, owner) {
      // Only-if-owner: a tab-switch closes one host while the other may already own the
      // gutter; an unconditional release would clobber the live owner's reservation.
      if (state.inspectionFilterGutterOwner === owner) state.inspectionFilterGutterOwner = null;
    },
    REQUEST_INSPECTION_FILTER_MINIMIZE(state) {
      state.inspectionFilterMinimizeRequestId += 1;
    },

    // === RESET MUTATIONS ===
    RESET_NAVIGATION_STATE(state) {
      state.isRailExpanded = false;
      state.activeGroup = null;
      state.panels = [];
      state.arePanelsCollapsed = false;
      state.isPinned = false;
      state.pinnedPanelIds = [];
      state.isQuickSettingsVisible = false;
      state.ghostOverlayOpenRequest = false;
    },

    // Legacy mutation names (backward compatibility)
    SET_PRIMARY_PALETTE_OPEN(state, val) {
      state.isRailExpanded = val;
    },
    SET_SECONDARY_PALETTE_OPEN(state, val) {
      state.arePanelsCollapsed = !val;
    },
    SET_ACTIVE_SECONDARY_PANEL() {
      // This is now handled by ADD_PANEL/REMOVE_PANEL
      // Kept for backward compatibility but does nothing
    },
  },

  actions: {
    // === RAIL ACTIONS ===

    /**
     * Expand the rail with a specific group's items
     * @param {string} groupId - The group to show items for
     */
    EXPAND_RAIL({ commit }, groupId) {
      commit('SET_ACTIVE_GROUP', groupId);
      commit('SET_RAIL_EXPANDED', true);
    },

    /**
     * Collapse the rail back to 50px
     */
    COLLAPSE_RAIL({ commit }) {
      commit('SET_RAIL_EXPANDED', false);
    },

    /**
     * Toggle a group's expansion state
     * @param {string} groupId - The group to toggle
     */
    TOGGLE_GROUP({ commit, state }, groupId) {
      if (state.activeGroup === groupId && state.isRailExpanded) {
        // Same group clicked while expanded - collapse
        commit('SET_RAIL_EXPANDED', false);
      } else {
        // Different group or collapsed - expand
        commit('SET_ACTIVE_GROUP', groupId);
        commit('SET_RAIL_EXPANDED', true);
      }
    },

    // === PANEL ACTIONS ===

    /**
     * Open a panel
     * @param {string|Object} options - Panel ID or { panelId, width?, position? }
     */
    OPEN_PANEL({ commit, state, dispatch }, options) {
      const { panelId, width = PANEL_WIDTH_DEFAULT } =
        typeof options === 'string' ? { panelId: options } : options;

      // Cancel pending requests when switching between tag-related panels
      // This prevents stale requests from completing after panel switch
      dispatch('CANCEL_TAG_PANEL_REQUESTS', panelId);

      // Already open?
      if (state.panels.some((p) => p.id === panelId)) {
        // If collapsed, expand
        if (state.arePanelsCollapsed) {
          commit('SET_PANELS_COLLAPSED', false);
        }
        return;
      }

      // At max capacity?
      if (state.panels.length >= state.maxPanels) {
        // Find first unpinned panel to replace
        const unpinnedPanel = state.panels.find(
          (p) => !state.pinnedPanelIds.includes(p.id)
        );

        if (unpinnedPanel) {
          // Remove the unpinned panel
          commit('REMOVE_PANEL', unpinnedPanel.id);
        } else {
          // All panels are pinned - show warning notification
          // Using Vue.notify if available, fallback to console
          if (typeof window !== 'undefined' && window.$notify) {
            window.$notify({
              title: 'All Panels Pinned',
              message: 'Unpin a panel to open a new one.',
              type: 'warning',
            });
          } else {
            console.warn('All panels are pinned. Unpin a panel to open a new one.');
          }
          return; // Cannot open new panel
        }
      }

      // Add the new panel
      commit('ADD_PANEL', { id: panelId, width });

      // Auto-pin Global Search (always pinned by default)
      if (panelId === PANEL_IDS.GLOBAL_SEARCH && !state.pinnedPanelIds.includes(panelId)) {
        state.pinnedPanelIds.push(panelId);
      }

      // Expand if collapsed
      if (state.arePanelsCollapsed) {
        commit('SET_PANELS_COLLAPSED', false);
      }

      // Collapse the rail
      dispatch('COLLAPSE_RAIL');
    },

    /**
     * Close a specific panel
     * Handles hierarchical panels (Tags) with step-back behavior
     * Note: Condition Map is NO LONGER hierarchical - uses side-by-side panels now
     * @param {string|Object} options - Panel ID string or options object { panelId, forceClose }
     * @param {string} options.panelId - The panel to close
     * @param {boolean} [options.forceClose=false] - If true, bypasses step-back logic and closes immediately
     */
    CLOSE_PANEL({ commit, state, dispatch, rootGetters }, options) {
      // Support both string (panelId) and object ({ panelId, forceClose }) for backward compatibility
      const { panelId, forceClose = false } =
        typeof options === 'string'
          ? { panelId: options, forceClose: false }
          : options;

      // Skip step-back logic if forceClose is true (Close button)
      // Only apply step-back logic for Back button navigation
      if (!forceClose) {
        // Handle hierarchical panels - check if we should step back instead of closing
        if (panelId === PANEL_IDS.TAGS) {
          const editingTag = rootGetters['GET_EDITING_TAG'];
          const selectedType = rootGetters['TAGS_TYPES/GET_SELECTED_TYPE'];
          const selectedGroup = rootGetters['TAGS_GROUPS/GET_SELECTED_GROUP'];
          if (editingTag || selectedType || selectedGroup) {
            // Step back within Tags panel instead of closing
            dispatch('TAGS_TYPES/ONE_STEP_BACK', null, { root: true });
            return;
          }
        }
        // Note: CONDITION_MAP removed from hierarchical handling
        // It now uses separate side-by-side panels (INSPECTION_INFO, AVA_INSPECTION_INFO)
      }

      // Standard close - remove panel entirely
      commit('REMOVE_PANEL', panelId);

      // Clean up pin state for this panel
      commit('UNPIN_PANEL', panelId);

      // Clear stale tag data to prevent thumbnail rendering bug on reopen
      // (stale SAS token URLs cause v-img destroy/recreate inside tooltip, which gets stuck)
      // Only once nothing is left to show it: a tag-type panel renders this very list, so
      // clearing on the Tags panel's close would empty a list the user never closed.
      // REMOVE_PANEL has already run above, so state.panels is what survives this close.
      if (
        (isTagTypePanel(panelId) || panelId === PANEL_IDS.TAGS) &&
        !state.panels.some((p) => isTagTypePanel(p.id))
      ) {
        commit('SET_FILTERED_TAGS', [], { root: true });
      }

      // Clear editing state when force closing
      if (forceClose) {
        if (panelId === PANEL_IDS.TAGS) {
          dispatch('CLEAR_EDIT_TAG_TAB', null, { root: true });
          commit('TAGS_TYPES/SET_SELECTED_TYPE', null, { root: true });
          commit('TAGS_GROUPS/SET_SELECTED_GROUP', null, { root: true });
        }
      }

      // Clear editing state when closing inspection info panels
      if (panelId === PANEL_IDS.INSPECTION_INFO || panelId === PANEL_IDS.AVA_INSPECTION_INFO) {
        dispatch('clearEditAMISTagTab', null, { root: true });
      }

      // Clear pin if no panels remain
      if (state.panels.length === 0) {
        commit('SET_IS_PINNED', false);
      }
    },

    /**
     * Close all panels
     */
    CLOSE_ALL_PANELS({ commit }) {
      commit('CLEAR_PANELS');
      commit('SET_IS_PINNED', false);
      commit('SET_RAIL_EXPANDED', false);
      commit('SET_ACTIVE_GROUP', null);
    },

    /**
     * Resize a panel
     * @param {Object} payload - { panelId, width }
     */
    RESIZE_PANEL({ commit }, { panelId, width }) {
      commit('SET_PANEL_WIDTH', { panelId, width });
    },

    // === COLLAPSE/EXPAND ACTIONS ===

    /**
     * Collapse all panels (hide but preserve state)
     */
    COLLAPSE_PANELS({ commit }) {
      commit('SET_PANELS_COLLAPSED', true);
    },

    /**
     * Expand panels (restore from collapsed state)
     */
    EXPAND_PANELS({ commit }) {
      commit('SET_PANELS_COLLAPSED', false);
    },

    /**
     * Toggle panels collapsed state
     */
    TOGGLE_PANELS_COLLAPSED({ commit, state }) {
      commit('SET_PANELS_COLLAPSED', !state.arePanelsCollapsed);
    },

    // === CONFIGURATION ACTIONS ===

    /**
     * Set maximum number of panels allowed
     * @param {number} count - Max panels (1-5)
     */
    SET_MAX_PANELS({ commit }, count) {
      commit('SET_MAX_PANELS', count);
    },

    // === PIN ACTIONS ===

    /**
     * Toggle pin state
     */
    TOGGLE_PIN({ commit, state }) {
      commit('SET_IS_PINNED', !state.isPinned);
    },

    // === TAG PANEL REQUEST CANCELLATION ===

    /**
     * Cancel pending HTTP requests when switching between tag-related panels.
     * This prevents stale requests from completing and causing state issues.
     *
     * @param {string} openingPanelId - The panel being opened
     */
    CANCEL_TAG_PANEL_REQUESTS({ dispatch }, openingPanelId) {
      // All tag-related store modules that support request cancellation
      const TAG_TYPE_MODULES = [
        'ANOMALY_VIEW',
        'EQUIPMENT_VIEW',
        'PIPELINE_VIEW',
        'STRUCTURAL_VIEW',
        'OPERATION_VIEW',
        'GROUP_VIEW',
      ];

      const INSPECTION_MODULES = [
        'AVA_INSPECT',
        'INSPECTION_VIEW',
      ];

      // Check what type of panel is being opened
      const isOpeningTagTypePanel = isTagTypePanel(openingPanelId);
      const isOpeningConditionMap = openingPanelId === PANEL_IDS.CONDITION_MAP;

      // When opening a tag type panel, cancel inspection requests
      if (isOpeningTagTypePanel) {
        INSPECTION_MODULES.forEach(namespace => {
          dispatch(`${namespace}/CANCEL_CURRENT_REQUEST`, null, { root: true });
        });
        // Also cancel other tag type requests
        TAG_TYPE_MODULES.forEach(namespace => {
          dispatch(`${namespace}/CANCEL_CURRENT_REQUEST`, null, { root: true });
        });
      }

      // When opening Condition Map, cancel tag type requests
      if (isOpeningConditionMap) {
        TAG_TYPE_MODULES.forEach(namespace => {
          dispatch(`${namespace}/CANCEL_CURRENT_REQUEST`, null, { root: true });
        });
      }
    },

    // === BACKWARD COMPATIBILITY ACTIONS ===
    // These maintain API compatibility with existing code

    /**
     * @deprecated Use TOGGLE_GROUP instead
     */
    OPEN_GROUP({ dispatch }, groupId) {
      dispatch('TOGGLE_GROUP', groupId);
    },

    /**
     * @deprecated Use COLLAPSE_RAIL instead
     */
    CLOSE_PRIMARY_PALETTE({ dispatch }) {
      dispatch('COLLAPSE_RAIL');
    },

    /**
     * @deprecated Use OPEN_PANEL instead
     */
    OPEN_SECONDARY_PANEL({ dispatch }, panelId) {
      dispatch('OPEN_PANEL', panelId);
    },

    /**
     * @deprecated Use CLOSE_PANEL instead
     */
    CLOSE_SECONDARY_PANEL({ dispatch, state }) {
      if (state.panels.length > 0) {
        // Close the last panel
        dispatch('CLOSE_PANEL', state.panels[state.panels.length - 1].id);
      }
    },

    /**
     * @deprecated Use CLOSE_ALL_PANELS instead
     */
    CLOSE_ALL_PALETTES({ dispatch }) {
      dispatch('CLOSE_ALL_PANELS');
    },
  },
};
